import {
  OPEN_WORK_MAP_WORK_CARD_ID,
  RIDER_DAY_TRIPS_CARD_ID,
  RIDER_DAY_TRIP_JOBS_CARD_ID,
  RIDER_DAY_TRIP_LEGS_CARD_ID,
  RIDER_DAY_TRIP_SEGMENTS_CARD_ID,
  RIDER_DAY_TRIP_SUMMARY_CARD_ID,
} from './cardIds';

/**
 * Per-card Metabase template-tag parameter ids.
 *
 * Metabase's query API (pMBQL `stages` cards) binds a provided `parameters[]`
 * entry to a card template-tag by matching the parameter's `id` to that
 * template-tag's stored id — NOT by the `target`/tag name. Sending the tag name
 * (or any other id) leaves the tag unfilled: a required tag then 400s with
 * `missing required parameters: #{"…"}`, an optional one is silently dropped.
 *
 * The ids are author-assigned and arbitrary per card — some cards use friendly
 * slugs (`ds-1`, `tt-ds`), others UUIDs, others the tag name itself — so they
 * must be recorded explicitly. Keyed by the numeric Cube card id (see
 * {@link ./cardIds}), then by template-tag name → stored parameter id.
 *
 * Source of truth is the card in Metabase (cube.lyskraft.in):
 *   GET /api/card/:id → dataset_query.stages[].`template-tags`{[k].name → [k].id}
 * When a card here is superseded/rebuilt (new id in cardIds.ts), refresh its
 * entry below from that card's template-tags. Cards with no template-tags
 * (constant-payload cards) are intentionally absent.
 */
const OPEN_WORK_MAP_FACILITY_IDS_PARAMS = { facility_ids: 'tt-facility-ids' };
const RIDER_DAY_PARAMS = { rider_id: 'tt-rider-id', business_date: 'tt-business-date' };

export const CUBE_CARD_PARAM_IDS: Record<number, Record<string, string>> = {
  /* Depth Breadth Daily v3: tag ids are the tag names. */
  10226: { facility: 'facility', date_start: 'date_start', date_end: 'date_end' },
  /* Depth Breadth Live v3: tag id is the tag name. */
  10227: { facility: 'facility' },
  9955: OPEN_WORK_MAP_FACILITY_IDS_PARAMS,
  ...(OPEN_WORK_MAP_WORK_CARD_ID === 9955
    ? {}
    : { [OPEN_WORK_MAP_WORK_CARD_ID]: OPEN_WORK_MAP_FACILITY_IDS_PARAMS }),
  9956: { facility_ids: 'tt-facility-ids' },
  9985: { rider_id: 'tt-rider-id', business_date: 'tt-business-date' },
  10001: { rider_id: 'tt-rider-id', business_date: 'tt-business-date' },
  [RIDER_DAY_TRIP_SUMMARY_CARD_ID]: RIDER_DAY_PARAMS,
  [RIDER_DAY_TRIPS_CARD_ID]: RIDER_DAY_PARAMS,
  [RIDER_DAY_TRIP_LEGS_CARD_ID]: RIDER_DAY_PARAMS,
  [RIDER_DAY_TRIP_JOBS_CARD_ID]: RIDER_DAY_PARAMS,
  [RIDER_DAY_TRIP_SEGMENTS_CARD_ID]: RIDER_DAY_PARAMS,
  10002: {
    rider_id: 'tt-rider-id',
    business_date: 'tt-business-date',
    shift_id: 'tt-shift-id',
    shift_started_at: 'tt-shift-started-at',
    shift_ended_at: 'tt-shift-ended-at',
  },
  6965: { date_start: 'ds-1', date_end: 'de-1' },
  6966: { date_start: 'ds-1', date_end: 'de-1' },
  6967: { date_start: 'ds-1', date_end: 'de-1' },
  6968: { date_start: 'ds-1', date_end: 'de-1' },
  6969: { date_start: 'ds-1', date_end: 'de-1', brand_managers: 'bm-1' },
  6970: {
    date_start: 'ds-1',
    date_end: 'de-1',
    brand_names: 'brand-names-1',
    merch_portfolios: 'merch-portfolios-1',
    store_names: 'store-names-1',
    brand_managers: 'bm-1',
  },
  6974: { date_start: 'ds-1', date_end: 'de-1' },
  6975: { date_start: 'ds-1', ai_tag: 'tag-1', date_end: 'de-1' },
  7437: { date_start: 'ds-1', date_end: 'de-1' },
  7440: { date_start: 'ds-1', date_end: 'de-1' },
  7443: { date_start: 'ds-1', date_end: 'de-1' },
  7446: { entity_kind: 'tag-entity-kind', entity_id: 'tag-entity-id' },
  7497: { date_range: 'date_range', brand_name: 'brand_name' },
  7498: { date_range: 'date_range', brand_name: 'brand_name' },
  7499: { date_range: 'date_range', brand_name: 'brand_name' },
  7501: {
    date_range: 'date_range',
    brand_name: 'brand_name',
    vendor_packing_outcome: 'vendor_packing_outcome',
    vendor_packing_tat_bucket: 'vendor_packing_tat_bucket',
    sales_channel_order_id: 'sales_channel_order_id',
    package_id: 'package_id',
  },
  7758: { date_range: 'date_range', brand_name: 'brand_name' },
  7759: { date_range: 'date_range', brand_name: 'brand_name' },
  7760: { date_range: 'date_range', brand_name: 'brand_name' },
  7761: { date_range: 'date_range', brand_name: 'brand_name' },
  7762: { date_range: 'date_range', brand_name: 'brand_name' },
  7763: {
    brand_name: 'brand_name',
    brand_manager: 'brand_manager',
    merch_portfolio: 'merch_portfolio',
    date_start: 'date_start',
    date_end: 'date_end',
  },
  7764: {
    brand_name: 'brand_name',
    brand_manager: 'brand_manager',
    merch_portfolio: 'merch_portfolio',
    date_start: 'date_start',
    date_end: 'date_end',
  },
  8127: { date_start: 'tt-0-date_start', date_end: 'tt-1-date_end' },
  8129: { date_start: 'tt-0-date_start', date_end: 'tt-1-date_end' },
  9370: { date_start: 'tt-ds', date_end: 'tt-de' },
  9371: { date_start: 'tt-ds', date_end: 'tt-de' },
  9392: {
    date_start: 'a4b81219-9e99-4f25-8ad9-7d50cdaff6a3',
    date_end: 'f493073b-926e-4392-9002-92bebc3d9548',
  },
  9397: {
    date_start: '064d2e35-0b7e-4769-998d-c7f1614234fe',
    date_end: 'de5de6e4-cbe5-40a1-862a-c8e2a78bf7a3',
  },
  9419: {
    date_start: 'ddbc4d6f-00ef-43b0-b05b-caf32cbc80e9',
    date_end: '17ecd305-50c3-4e0b-b09a-9944e818cc3f',
  },
  9651: { date_start: 'tt-ds', date_end: 'tt-de', brand_names: 'tt-bn' },
  /* 10094 is a copy of 9419, so it keeps 9419's template-tag ids. */
  10094: {
    date_start: 'ddbc4d6f-00ef-43b0-b05b-caf32cbc80e9',
    date_end: '17ecd305-50c3-4e0b-b09a-9944e818cc3f',
  },
  10096: { dc_id: '060d602a-374a-4ed9-b862-45768450f1e1' },
  /* 10100 is a copy of 10094 (same template-tag ids). */
  10100: {
    date_start: 'ddbc4d6f-00ef-43b0-b05b-caf32cbc80e9',
    date_end: '17ecd305-50c3-4e0b-b09a-9944e818cc3f',
  },
  10102: { dc_id: '732c710d-18ef-4863-b771-b3f5fb6de0ac' },
  /* 10104 is a copy of 10100 (same template-tag ids). */
  10104: {
    date_start: 'ddbc4d6f-00ef-43b0-b05b-caf32cbc80e9',
    date_end: '17ecd305-50c3-4e0b-b09a-9944e818cc3f',
  },
  /* 10107 is a copy of 10104 (same template-tag ids). */
  10107: {
    date_start: 'ddbc4d6f-00ef-43b0-b05b-caf32cbc80e9',
    date_end: '17ecd305-50c3-4e0b-b09a-9944e818cc3f',
  },
  /* 10109 is a copy of 10107 (same template-tag ids). */
  10109: {
    date_start: 'ddbc4d6f-00ef-43b0-b05b-caf32cbc80e9',
    date_end: '17ecd305-50c3-4e0b-b09a-9944e818cc3f',
  },
  /* Online Performance traffic cards. */
  10118: { date_start: 'tt-date_start', date_end: 'tt-date_end' },
  10119: { date_start: 'tt-date_start', date_end: 'tt-date_end' },
  10126: { date_start: 'tt-date_start', date_end: 'tt-date_end' },
};
